import React from 'react';
import { Driver, Order, SystemConfig } from '../../types';
import { TrendingUp, Clock, Hash } from 'lucide-react';
import { getDriverDeliveredOrders } from '../../services/driverDailyStats';
import { calculateDriverDailyEarnings } from '../../services/driverCompensation';

interface Props {
    drivers: Driver[];
    orders: Order[];
    config: SystemConfig;
}

export const DriverPerformanceTable: React.FC<Props> = ({ drivers, orders, config }) => {
    const calculations = drivers.map(driver => {
        // Filter orders for this driver in the current period
        const driverOrders = getDriverDeliveredOrders(orders, driver.id);
        const totalDeliveries = driverOrders.length;
        const { dropFee, totalDropEarnings, totalDailyEarnings, totalEarnings } = calculateDriverDailyEarnings(config, totalDeliveries);

        // Performance Metrics
        const avgSla = driverOrders.length > 0
            ? driverOrders.reduce((acc, o) => acc + (o.deliveredAt && o.createdAt ? (o.deliveredAt - o.createdAt) / 60000 : 0), 0) / driverOrders.length
            : 0;

        // 1. Avg Service Time (Tempo na Porta)
        const serviceTimes = driverOrders
            .filter(o => o.deliveredAt && o.arrivedAt)
            .map(o => (o.deliveredAt! - o.arrivedAt!) / 1000 / 60);

        const avgServiceTime = serviceTimes.length > 0
            ? serviceTimes.reduce((a, b) => a + b, 0) / serviceTimes.length
            : 0;

        // 2. Avg Speed (km/h) & Speed Factor (vs Google)
        let totalDistanceKm = 0;
        let totalRealDurationHours = 0;
        let totalGoogleDurationSeconds = 0;
        let totalRealDurationSeconds = 0;

        driverOrders.forEach(o => {
            if (o.departedHubAt && o.deliveredAt && o.distance) {
                totalDistanceKm += (o.distance / 1000); // metros para km
                const durationHours = (o.deliveredAt - o.departedHubAt) / 1000 / 3600;
                totalRealDurationHours += durationHours;

                const realDurationSec = (o.deliveredAt - o.departedHubAt) / 1000;
                totalRealDurationSeconds += realDurationSec;
                totalGoogleDurationSeconds += (o.estimatedDuration || realDurationSec); // fallback se google falhar
            }
        });

        const avgSpeedKmH = totalRealDurationHours > 0 ? totalDistanceKm / totalRealDurationHours : 0;

        // Fator: Google / Real.
        // Se Google diz 1000s e Real faz em 800s -> 1000/800 = 1.25 (25% mais rápido)
        // Se Google diz 1000s e Real faz em 2000s -> 1000/2000 = 0.5 (50% da velocidade esperada)
        const speedFactor = totalRealDurationSeconds > 0 ? totalGoogleDurationSeconds / totalRealDurationSeconds : 1;

        return {
            ...driver,
            analytics: {
                totalDeliveries,
                totalDropEarnings,
                totalDailyEarnings,
                totalEarnings,
                dropFee,
                avgSla,
                avgServiceTime,
                avgSpeedKmH,
                speedFactor
            }
        };
    }).sort((a, b) => b.analytics.totalEarnings - a.analytics.totalEarnings);

    return (
        <div className="bg-card-bg rounded-[2.5rem] border border-border-dim overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-border-dim bg-sal-gunmetal-light/10 flex justify-between items-center">
                <h3 className="text-xl font-black text-text-primary uppercase tracking-widest flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-sal-cyber-teal" /> Performance da Frota
                </h3>
                <div className="text-xs text-text-muted font-bold uppercase">Ordenado por Faturamento</div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead>
                        <tr className="border-b border-border-dim text-xs font-black text-text-muted uppercase tracking-widest bg-sal-gunmetal-light/5">
                            <th className="p-4 pl-6">Motoboy</th>
                            <th className="p-4 text-center">Entregas</th>
                            <th className="p-4 text-center">Tempo Porta</th>
                            <th className="p-4 text-center">Velocidade</th>
                            <th className="p-4 text-center">Fator Vel.</th>
                            <th className="p-4 text-right pr-6">Total a Pagar</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border-dim">
                        {calculations.map(driver => (
                            <tr key={driver.id} className="group hover:bg-sal-gunmetal-light/5 transition-colors">
                                <td className="p-4 pl-6">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${driver.isAvailable ? 'bg-status-ready/10 text-status-ready' : 'bg-input-border/20 text-text-muted'}`}>
                                            {driver.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="text-base font-bold text-text-primary">{driver.name}</div>
                                            <div className="text-xs text-text-muted font-medium flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> Ciclo: {driver.analytics.avgSla.toFixed(0)}m
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4 text-center">
                                    <div className="inline-flex items-center gap-1 px-3 py-1 bg-input-bg rounded-lg border border-border-dim">
                                        <Hash className="w-3 h-3 text-sal-cyber-teal" />
                                        <span className="text-base font-black text-text-primary">{driver.analytics.totalDeliveries}</span>
                                    </div>
                                </td>
                                <td className="p-4 text-center text-text-secondary font-mono text-sm">
                                    {driver.analytics.avgServiceTime.toFixed(1)} min
                                </td>
                                <td className="p-4 text-center text-text-secondary font-mono text-sm">
                                    {driver.analytics.avgSpeedKmH.toFixed(0)} km/h
                                </td>
                                <td className="p-4 text-center">
                                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-black uppercase ${driver.analytics.speedFactor >= 1.1 ? 'bg-status-ready/10 text-status-ready' : driver.analytics.speedFactor <= 0.9 ? 'bg-status-critical/10 text-status-critical' : 'bg-input-border/20 text-text-muted'}`}>
                                        {driver.analytics.speedFactor.toFixed(2)}x
                                    </div>
                                </td>
                                <td className="p-4 text-right pr-6">
                                    <div className="text-status-ready font-black font-mono text-xl flex items-center justify-end gap-1">
                                        <span className="text-sm opacity-50">R$</span>
                                        {driver.analytics.totalEarnings.toFixed(2)}
                                    </div>
                                    <div className="text-xs text-text-muted font-mono mt-0.5 flex justify-end items-center gap-1 opacity-60">
                                        <span>({driver.analytics.totalDeliveries}un × {driver.analytics.dropFee.toFixed(2)})</span>
                                        <span>+</span>
                                        <span>{driver.analytics.totalDailyEarnings.toFixed(2)}</span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {calculations.length === 0 && (
                            <tr>
                                <td colSpan={5} className="p-8 text-center text-text-muted text-sm uppercase tracking-widest">
                                    Nenhum dado para o período selecionado
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
